import { type ChangeEvent, type RefObject, useMemo, useRef } from 'react';

import { Button } from '@/app/components/button';
import { Checkbox } from '@/app/components/forms/checkbox';
import { Icon } from '@/app/components/icons';
import type { AdvancedSearch } from '@/app/components/shared/dealer/utils/dealer-constant';
import { ICON_DEALER_TYPE_MAP } from '@/app/components/shared/dealer/utils/dealer-icons';
import { useClickOutside } from '@/app/hooks/useClickOutside';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';

export interface PopoverOptionsProps {
  className?: string;
  type: AdvancedSearch;
  selectedOptions: Set<string>;
  countOptions?: number;
  onSelectOptions: (
    event: ChangeEvent<HTMLInputElement>,
    hasParams?: boolean
  ) => void;
  onSearch: () => void;
  onReset: (type: AdvancedSearch) => void;
  onClose: () => void;
  onCloseOutside: () => void;
  toggleOptionsRef?: RefObject<HTMLLabelElement | null>;
}

export const PopoverOptions = ({
  className,
  type,
  selectedOptions,
  onSelectOptions,
  countOptions = 0,
  onSearch,
  onReset,
  onClose,
  onCloseOutside,
  toggleOptionsRef,
}: PopoverOptionsProps) => {
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const options = useMemo(() => {
    return (type.options || [])?.map((item) => {
      return {
        ...item,
        name: item.name,
        label: item.label,
        checked: selectedOptions.has(item.name),
      };
    });
  }, [type.options, selectedOptions]);

  const handleOnSearch = () => {
    onSearch();
    onClose();
  };

  useClickOutside(popoverRef, onCloseOutside, {
    enable: true,
    exceptList: toggleOptionsRef?.current ? [toggleOptionsRef.current] : [],
  });

  return (
    <div
      ref={popoverRef}
      className={cn('bg-silver-50 w-full rounded-sm p-4 shadow-md', className)}
      {...createDataAttributes({ test: 'popover-options' })}
    >
      <div className="flex gap-x-2">
        <Icon
          className="size-5"
          icon={
            ICON_DEALER_TYPE_MAP[type.icon as keyof typeof ICON_DEALER_TYPE_MAP]
          }
        />
        <span className="text-body-1-fixed font-noto-bold">{type.label}</span>
      </div>

      <Checkbox
        orientation="vertical"
        className="flex-row-reverse justify-between py-4"
        name="popover-options"
        onChangeInput={(event) => onSelectOptions(event, false)}
        options={options}
      />

      <div className="flex flex-nowrap items-center gap-x-4">
        <Button
          width="full"
          size="xs"
          variant="secondary"
          color="black"
          onClick={() => onReset(type)}
          disabled={countOptions <= 0}
          {...createDataAttributes({ test: 'reset' })}
        >
          รีเซ็ต {countOptions > 0 ? `(${countOptions})` : ''}
        </Button>
        <Button
          width="full"
          size="xs"
          variant="primary"
          onClick={handleOnSearch}
          {...createDataAttributes({ test: 'submit' })}
        >
          ตกลง
        </Button>
      </div>
    </div>
  );
};
